import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import { CihClosingCta } from "~/components/cih/closing-cta";
import { CihProgramList } from "~/components/cih/program-rows";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { Button } from "~/components/ui/button";
import {
  getFrameworkCtpsFn,
  type FrameworkRow,
  type FrameworkWithRelations,
} from "~/lib/functions/cih/frameworks";
import { createBrandMeta } from "~/lib/shared/meta";

type LoaderData = {
  title: string;
  framework: FrameworkRow;
  ctps: FrameworkWithRelations["ctps"];
};

export const Route = createFileRoute(
  "/_public/frameworks/$slug/training-programs",
)({
  staticData: { title: "Training programs" },
  loader: async ({ params }): Promise<LoaderData> => {
    const { framework, ctps } = await getFrameworkCtpsFn({
      data: { slug: params.slug },
    });
    if (!framework) throw notFound();
    return { title: `${framework.name} training programs`, framework, ctps };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { slug: string };
  }) => {
    const name = loaderData?.framework.name ?? params.slug;
    return createBrandMeta({
      title: `${name} training programs | Dream Coach Match`,
      description: `All coach training programs that teach ${name}.`,
      ogUrl: `https://dreamcoachmatch.com/frameworks/${params.slug}/training-programs`,
      canonical: `https://dreamcoachmatch.com/frameworks/${params.slug}/training-programs`,
    });
  },
  component: FrameworkTrainingProgramsPage,
});

function FrameworkTrainingProgramsPage() {
  const { framework, ctps } = Route.useLoaderData();
  const { slug } = Route.useParams();

  return (
    <>
      <section className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 pb-12 md:pt-10 md:pb-16 xl:px-0">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Frameworks", href: "/frameworks" },
              { label: framework.name, href: `/frameworks/${slug}` },
              { label: "Training programs" },
            ]}
            isBold={false}
          />
          <div className="mt-6 text-xs font-medium tracking-[0.22em] text-purple-700 uppercase">
            ● {framework.name}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-medium tracking-tight text-gray-900 md:text-4xl lg:text-5xl xl:text-6xl">
            {ctps.length} programs teaching {framework.name}
          </h1>
          <div className="mt-6 flex flex-wrap gap-6 text-sm">
            <Link
              to="/frameworks/$slug/coaches"
              params={{ slug }}
              className="font-medium text-purple-700 hover:text-purple-800"
            >
              See coaches who use {framework.name} →
            </Link>
          </div>
        </div>
      </section>

      <CihProgramList programs={ctps} />

      <CihClosingCta
        headline={`Find a coach trained in ${framework.name}.`}
        cta={
          <Link
            to="/discovery"
            search={{ source: `framework:${slug}:programs` }}
          >
            <Button variant="primary" size="lg">
              Start →
            </Button>
          </Link>
        }
      />
    </>
  );
}
